import {
  addWeeks,
  differenceInCalendarWeeks,
  eachDayOfInterval,
  format,
  isSameDay,
  startOfWeek,
} from 'date-fns';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Platform,
  type ScrollView,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';

import { HorizontalScrollView, Stack } from '@/components/layout';
import { createStyles, useScaling } from '@/hooks';
import { colors, withOpacity } from '@/theme/colors';
import WeekDayCell from './week-day-cell';

/** Weeks generated either side of today — enough to swipe a year in each direction. */
const WEEK_RANGE = 26;
const WEEK_OPTIONS = { weekStartsOn: 1 } as const;

export type WeekStripProps = {
  selectedDate: Date;
  today: Date;
  /** `yyyy-MM-dd` keys that have at least one event, for the dot under the day. */
  eventDates: Set<string>;
  onSelectDate: (date: Date) => void;
};

/**
 * Mon–Sun strip above the day timeline. Swiping only browses weeks; tapping a day is
 * the sole action that changes the selection and the timeline data below.
 */
function WeekStrip({ selectedDate, today, eventDates, onSelectDate }: WeekStripProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const [width, setWidth] = useState(0);

  const weeks = useMemo(() => {
    const base = startOfWeek(today, WEEK_OPTIONS);
    return Array.from({ length: WEEK_RANGE * 2 + 1 }, (_, index) => {
      const start = addWeeks(base, index - WEEK_RANGE);
      return eachDayOfInterval({ start, end: addWeeks(start, 1) }).slice(0, 7);
    });
  }, [today]);

  const selectedWeekIndex = useMemo(() => {
    const offset = differenceInCalendarWeeks(selectedDate, today, WEEK_OPTIONS);
    return Math.min(Math.max(WEEK_RANGE + offset, 0), weeks.length - 1);
  }, [selectedDate, today, weeks.length]);

  const scrollRef = useRef<ScrollView>(null);
  const visibleIndexRef = useRef(selectedWeekIndex);
  const positionedWidthRef = useRef(0);

  // Keep the selected week aligned on first layout, resize, or an external selection.
  useEffect(() => {
    if (
      width <= 0 ||
      (positionedWidthRef.current === width && visibleIndexRef.current === selectedWeekIndex)
    ) {
      return;
    }

    const didWidthChange = positionedWidthRef.current !== width;
    positionedWidthRef.current = width;
    visibleIndexRef.current = selectedWeekIndex;
    const frame = requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({
        x: selectedWeekIndex * width,
        animated: !didWidthChange,
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedWeekIndex, width]);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const nextWidth = event.nativeEvent.layout.width;
    setWidth(previous => (previous === nextWidth ? previous : nextWidth));
  }, []);

  // Browsing a week only records the visible page. Selection changes exclusively on tap.
  const handleMomentumEnd = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (width <= 0) return;
      visibleIndexRef.current = Math.round(event.nativeEvent.contentOffset.x / width);
    },
    [width],
  );

  return (
    <Stack
      paddingTop={scale(4)}
      paddingRight={scale(4)}
      paddingBottom={scale(8)}
      paddingLeft={scale(4)}
      backgroundColor={withOpacity(colors['0'], 0.5)}
      style={styles.card}>
      {Platform.OS === 'android' ? null : (
        <Stack
          viewType="liquid-glass"
          pointerEvents="none"
          backgroundColor={withOpacity(colors['0'], 0.5)}
          overlayColor={withOpacity(colors['0'], 0.5)}
          intensity={20}
          tint="light"
          style={styles.glassBackground}
        />
      )}
      <Stack onLayout={handleLayout}>
        {width > 0 ? (
          <HorizontalScrollView
            ref={scrollRef}
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            onMomentumScrollEnd={handleMomentumEnd}>
            {weeks.map(week => (
              <Stack key={week[0].toISOString()} direction="row" gap={scale(4)} width={width}>
                {week.map(day => (
                  <WeekDayCell
                    key={day.toISOString()}
                    day={day}
                    isSelected={isSameDay(day, selectedDate)}
                    isToday={isSameDay(day, today)}
                    hasEvent={eventDates.has(format(day, 'yyyy-MM-dd'))}
                    onSelectDate={onSelectDate}
                  />
                ))}
              </Stack>
            ))}
          </HorizontalScrollView>
        ) : null}
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  card: {
    borderRadius: scale(12),
    overflow: 'hidden',
    borderCurve: 'continuous',
  },
  glassBackground: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
}));

export default memo(WeekStrip);
